import { OktaAuth, AccessToken } from '@okta/okta-auth-js';

const oktaAuth = new OktaAuth({
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID!,
  issuer: process.env.REACT_APP_OKTA_ISSUER!,
  redirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI!,
  scopes: ['openid', 'profile', 'email'],
  pkce: true,
  responseType: 'code',
});

export async function handleLoginCallback(): Promise<string> {
  if (oktaAuth.isLoginRedirect()) {
    await oktaAuth.handleLoginRedirect();
    // Check localStorage first (set by sub-apps like /drc), then Okta state
    const storedUri = localStorage.getItem('rdds_original_uri');
    localStorage.removeItem('rdds_original_uri');
    const originalUri = storedUri || oktaAuth.getOriginalUri() || '/';
    oktaAuth.removeOriginalUri();
    return originalUri;
  }
  return '/';
}

export async function getAccessToken(): Promise<string | undefined> {
  const tokenManager = oktaAuth.tokenManager;
  const token = await tokenManager.get('accessToken');
  if (token && 'accessToken' in token && !tokenManager.hasExpired(token)) {
    return (token as AccessToken).accessToken;
  }
  try {
    const renewed = await tokenManager.renew('accessToken');
    if (renewed && 'accessToken' in renewed) {
      return (renewed as AccessToken).accessToken;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export async function validateSession(): Promise<boolean> {
  try {
    const authed = await oktaAuth.isAuthenticated();
    if (!authed) return false;
    await oktaAuth.tokenManager.renew('accessToken');
    return true;
  } catch {
    await oktaAuth.tokenManager.clear();
    return false;
  }
}

export async function isAuthenticated(): Promise<boolean> {
  return oktaAuth.isAuthenticated();
}

export async function getUser(): Promise<{ email: string; name: string } | null> {
  const userInfo = await oktaAuth.getUser();
  if (!userInfo) return null;
  return {
    email: (userInfo.email as string) || '',
    name: (userInfo.name as string) || (userInfo.preferred_username as string) || '',
  };
}

export async function signIn(originalUri?: string): Promise<void> {
  if (originalUri) {
    localStorage.setItem('rdds_original_uri', originalUri);
  }
  await oktaAuth.signInWithRedirect();
}

export async function signOut(): Promise<void> {
  await oktaAuth.signOut({
    postLogoutRedirectUri: window.location.origin + '/',
    clearTokensBeforeRedirect: true,
  });
}

export { oktaAuth };
